import { Box, Flex, IconButton, Text, Tooltip, VStack, useMediaQuery } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useEffect, useState } from "react";

import AcceptedSvgIcon from "./icons/AcceptedSvgIcon";
import AdminInfoSvgIcon from "./icons/AdminInfoSvgIcon";
import ConsultantSvgIcon from "./icons/ConsultantSvgIcon";
import DashboardSvgIcon from "./icons/DashboardSvgIcon";
import { GlobalUtils } from "src/utils/globalUtils";
import MatrimonyProfilesSvgIcon from "./icons/MatrimonyProfilesSvgIcon";
import PendingSvgIcon from "./icons/PendingSvgIcon";
import RejectedSvgIcon from "./icons/RejectedSvgIcon";
import { Admin_Labels_Hindi } from "src/common/constants/hindiLabels";
import { Admin_Labels_Eng } from "src/common/constants/englishLabels";
import { useSelector } from "react-redux";
import { RootState } from "src/store/store";

const COLORS = {
  primary: "#4A2C2A", // deep red per screenshot
  secondary: "#F8F7F5", // sidebar bg
  accent: "#6B7280", // muted text
  border: "#E5E7EB",
};

interface MenuItem {
  key: string;
  label: string;
  IconExpanded: React.ComponentType<{ size?: number }>;
  IconCollapsed: React.ComponentType<{ size?: number }>;
}
export type SidebarProps = {
  activeKey?: string;
  onChange?: (key: string) => void;
  // parent can read width via onWidthChange; here we keep non-fixed layout so main content sits beside it
  onWidthChange?: (width: number) => void;
};

export const Sidebar: React.FC<SidebarProps> = ({
  activeKey = "dashboard",
  onChange,
  onWidthChange,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const token = localStorage.getItem("authToken");
  const isSuperAdmin = GlobalUtils.getAuthStateFromToken(token).role === "SUPER_ADMIN";
  const [isMobile] = useMediaQuery("(max-width: 1000px)");
  // const { isOpen, onOpen, onClose } = useDisclosure();

  const globalLanguage = useSelector(
    (state: RootState) => state.globalInfo.globalLanguage
  );
  const isHindiLabels = globalLanguage === "HI";

var MENU_ITEMS: MenuItem[] = [
  {
    key: "dashboard",
    label: isHindiLabels? Admin_Labels_Hindi.ADMIN_DASHBOARD:Admin_Labels_Eng.ADMIN_DASHBOARD,
    IconExpanded: DashboardSvgIcon,
    IconCollapsed: DashboardSvgIcon,
  },
  {
    key: "pending",
    label: isHindiLabels? Admin_Labels_Hindi.PENDING_APPROVALS: Admin_Labels_Eng.PENDING_APPROVALS,
    IconExpanded: PendingSvgIcon,
    IconCollapsed: PendingSvgIcon,
  },
  {
    key: "accepted",
    label: isHindiLabels? Admin_Labels_Hindi.ACCEPTED_USERS: Admin_Labels_Eng.ACCEPTED_USERS,
    IconExpanded: AcceptedSvgIcon,
    IconCollapsed: AcceptedSvgIcon,
  },
  {
    key: "rejected",
    label: isHindiLabels? Admin_Labels_Hindi.REJECTED_USERS: Admin_Labels_Eng.REJECTED_USERS,
    IconExpanded: RejectedSvgIcon,
    IconCollapsed: RejectedSvgIcon,
  },
];

  const menuItems = [
    ...MENU_ITEMS,
    ...(isSuperAdmin
      ? [
          {
            key: "matrimonyProfiles",
            label: isHindiLabels? Admin_Labels_Hindi.MATRIMONY_PROFILES: Admin_Labels_Eng.MATRIMONY_PROFILES,
            IconExpanded: MatrimonyProfilesSvgIcon,
            IconCollapsed: MatrimonyProfilesSvgIcon,
          },
          {
            key: "marriageConsultants",
            label: isHindiLabels ? Admin_Labels_Hindi.MARRIAGE_CONSULTANTS : Admin_Labels_Eng.MARRIAGE_CONSULTANTS,
            IconExpanded: ConsultantSvgIcon,
            IconCollapsed: ConsultantSvgIcon,
          },
          {
            key: "adminInfo",
            label: isHindiLabels? Admin_Labels_Hindi.ADMIN_INFO: Admin_Labels_Eng.ADMIN_INFO,
            IconExpanded: AdminInfoSvgIcon,
            IconCollapsed: AdminInfoSvgIcon,
          },
        ]
      : []),
  ];

  // On mobile, report zero width to parent; desktop reports actual width
  useEffect(() => {
    if (onWidthChange) onWidthChange(isMobile ? 0 : collapsed ? 70 : 240);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, collapsed]);

  // Auto collapse on small screens (desktop layout only)
  useEffect(() => {
    const update = () => setCollapsed(window.innerWidth < 1000);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const numericWidth = collapsed ? 70 : 240;
  const width = numericWidth + "px";

  return (
    <Box
      bg={COLORS.secondary}
      borderRight={`1px solid ${COLORS.border}`}
      transition="all 0.3s ease"
      width={width}
      minW={width}
      height="100vh" // ensure full page height, flush top & bottom
      overflowY="auto"
      display="flex"
      flexDirection="column"
    >
      <Flex
        align="center"
        justify={collapsed ? "center" : "space-between"}
        px={3}
        py={3}
      >
        {!collapsed && (
          <Text fontWeight="bold" color="#1F2937">
            {isHindiLabels? Admin_Labels_Hindi.ADMIN:Admin_Labels_Eng.ADMIN}
          </Text>
        )}
        <IconButton
          aria-label={collapsed ? "Expand" : "Collapse"}
          size="sm"
          onClick={() => setCollapsed(!collapsed)}
          icon={collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          bg={COLORS.primary}
          color="white"
          _hover={{ bg: "#4A2C2A" }}
          borderRadius="md"
        />
      </Flex>

      <VStack align="stretch" spacing={2} px={2} py={2} flex={1}>
        {menuItems.map((item) => {
          const active = activeKey === item.key;
          const IconComponent = collapsed ? item.IconCollapsed : item.IconExpanded;
          return (
            <Tooltip
              key={item.key}
              label={collapsed ? item.label : ""}
              placement="right"
              hasArrow
              openDelay={300}
            >
              <Flex
                align="center"
                gap={collapsed ? 0 : 10}
                px={4}
                py={2.5}
                borderRadius="md"
                cursor="pointer"
                onClick={() => onChange && onChange(item.key)}
                bg={active ? "rgba(139,0,0,0.08)" : "transparent"}
                _hover={{ bg: active ? "rgba(139,0,0,0.12)" : "#eceae7" }}
                borderLeft={
                  active
                    ? `4px solid ${COLORS.primary}`
                    : "4px solid transparent"
                }
                border={`1px solid ${active ? COLORS.border : "transparent"}`}
                justify={collapsed ? "center" : "flex-start"}
              >
                <Box color={active ? COLORS.primary : COLORS.accent}>
                  <IconComponent size={collapsed ? 22 : 20} />
                </Box>
                {!collapsed && (
                  <Text
                    color={active ? COLORS.primary : COLORS.accent}
                    fontWeight={active ? "600" : "500"}
                    fontSize="sm"
                  >
                    {item.label}
                  </Text>
                )}
              </Flex>
            </Tooltip>
          );
        })}
      </VStack>
    </Box>
  );
};

export default Sidebar;
